import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, LinearProgress, Stack, Typography } from '@mui/material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { api } from '../../shared/service/dataprovider';
import tokens from '../../shared/theme/tokens';

const ALLOWED_TYPES = ['image/png', 'image/jpeg', 'image/webp'];

/**
 * Per-variant banner requirements. Single source of truth for both the
 * client-side validation in this dialog and the hint text rendered next to
 * each banner section on the properties page.
 *
 * - landing: the pre-login split-hero image ({abbr}.png). Portrait — it fills
 *   the tall left panel of the sign-in screen.
 * - home: the post-login wide strip ({abbr}-home.png).
 *
 * `aspect` (width / height) is a SOFT target — images outside the tolerance
 * still upload, we just surface `aspectWarning`.
 */
export const BANNER_SPECS = {
  landing: {
    maxBytes: 5 * 1024 * 1024,
    minWidth: 800,
    aspect: 2 / 3,
    aspectTolerance: 0.4,
    hint: 'Portrait image — recommended 800×1200 (2:3) · max 5MB',
    aspectWarning: 'A portrait 2:3 image (e.g. 800×1200) fills the sign-in screen best — you can still upload it.',
  },
  home: {
    maxBytes: 5 * 1024 * 1024,
    minWidth: 1200,
    aspect: 4,
    aspectTolerance: 0.35,
    hint: 'Wide banner — recommended 1600×400 (4:1) · max 5MB',
    aspectWarning: 'A wide 4:1 banner (e.g. 1600×400) will crop best — you can still upload it.',
  },
};

const formatMb = bytes => `${Math.round(bytes / (1024 * 1024))}MB`;

/**
 * Decode a file's pixel dimensions off-screen via an <img> + object URL.
 * Resolves { width, height } or rejects if the bytes aren't a decodable image.
 */
function decodeImageDimensions(file) {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      const dims = { width: img.naturalWidth, height: img.naturalHeight };
      URL.revokeObjectURL(url);
      resolve(dims);
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('Could not read image'));
    };
    img.src = url;
  });
}

/**
 * Banner upload dialog: drag-and-drop or click to select, client-side
 * validation against BANNER_SPECS, an in-context placement preview, then a
 * three-step upload (signed URL → S3 PUT → upload-complete) that bumps the
 * cache-busting timestamp server-side.
 *
 * Props:
 * - open, variant ('landing' | 'home'), propertyId
 * - campaignKey (optional): when set, uploads GLOBAL campaign artwork instead
 *   of a per-property banner. Campaign artwork lives at a fixed key shared by
 *   every property, so uploading replaces it everywhere at once.
 * - onClose(): close + reset
 * - onUploaded(): called after a successful upload (page invalidates + closes)
 */
export default function BannerUploadDialog({ open, variant, propertyId, campaignKey = null, onClose, onUploaded }) {
  const queryClient = useQueryClient();
  const spec = BANNER_SPECS[variant] || BANNER_SPECS.landing;

  const [file, setFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [errors, setErrors] = useState([]); // hard blocks — disable Upload
  const [warnings, setWarnings] = useState([]); // soft — allow Upload
  const [decoding, setDecoding] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);
  // Generation token: invalidates in-flight dimension decodes when the user
  // swaps files or the dialog closes, so a slow decode can't clobber the
  // validation state of a newer selection (or of a reopened dialog).
  const decodeGenRef = useRef(0);

  // Reset everything when the dialog closes so the next open starts clean.
  useEffect(() => {
    if (!open) {
      decodeGenRef.current += 1;
      setFile(null);
      setErrors([]);
      setWarnings([]);
      setDecoding(false);
      setDragActive(false);
      setUploading(false);
      setPreviewUrl(prev => {
        if (prev) URL.revokeObjectURL(prev);
        return null;
      });
    }
  }, [open]);

  // Revoke the object URL on unmount.
  useEffect(() => {
    return () => {
      if (previewUrl) URL.revokeObjectURL(previewUrl);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const uploadUrlMutation = useMutation({
    mutationFn: ({ contentType }) =>
      campaignKey
        ? api.post(`/v1/admin/banner-campaigns/${campaignKey}/upload-url?contentType=${encodeURIComponent(contentType)}`)
        : api.post(`/v1/admin/properties/${propertyId}/banner-upload-url?variant=${variant}&contentType=${encodeURIComponent(contentType)}`),
  });

  const uploadCompleteMutation = useMutation({
    mutationFn: () =>
      campaignKey
        ? api.post(`/v1/admin/banner-campaigns/${campaignKey}/upload-complete`)
        : api.post(`/v1/admin/properties/${propertyId}/banner-upload-complete?variant=${variant}`),
  });

  const acceptFile = async selected => {
    if (!selected) return;
    const gen = ++decodeGenRef.current;

    // Swap in the new preview URL, revoking the previous one.
    setPreviewUrl(prev => {
      if (prev) URL.revokeObjectURL(prev);
      return URL.createObjectURL(selected);
    });
    setFile(selected);
    setWarnings([]);

    // Synchronous hard checks: type + size.
    const hardErrors = [];
    if (!ALLOWED_TYPES.includes(selected.type)) {
      hardErrors.push('Unsupported file type. Use PNG, JPEG, or WebP.');
    }
    if (selected.size > spec.maxBytes) {
      hardErrors.push(`File is too large — max ${formatMb(spec.maxBytes)}.`);
    }
    if (hardErrors.length) {
      setErrors(hardErrors);
      return;
    }

    // Async dimension checks.
    setErrors([]);
    setDecoding(true);
    try {
      const { width, height } = await decodeImageDimensions(selected);
      if (gen !== decodeGenRef.current) return; // superseded by a newer file or close
      const dimErrors = [];
      const dimWarnings = [];
      if (width < spec.minWidth) {
        dimErrors.push(`Image is only ${width}px wide — needs at least ${spec.minWidth}px.`);
      }
      if (spec.aspect) {
        const ratio = width / height;
        if (Math.abs(ratio - spec.aspect) > spec.aspect * spec.aspectTolerance) {
          dimWarnings.push(`This image is ${width}×${height}. ${spec.aspectWarning}`);
        }
      }
      setErrors(dimErrors);
      setWarnings(dimWarnings);
    } catch (err) {
      if (gen !== decodeGenRef.current) return;
      setErrors(['Could not read this image. Try a different file.']);
    } finally {
      if (gen === decodeGenRef.current) setDecoding(false);
    }
  };

  const handleFileInputChange = e => {
    acceptFile(e.target.files?.[0]);
    // Allow re-selecting the same file after a reset.
    e.target.value = '';
  };

  const handleDragOver = e => {
    e.preventDefault();
  };
  const handleDragEnter = e => {
    e.preventDefault();
    setDragActive(true);
  };
  const handleDragLeave = e => {
    e.preventDefault();
    setDragActive(false);
  };
  const handleDrop = e => {
    e.preventDefault();
    setDragActive(false);
    acceptFile(e.dataTransfer.files?.[0]);
  };

  const canUpload = !!file && !decoding && errors.length === 0 && !uploading;

  const handleUpload = async () => {
    if (!canUpload) return;
    setUploading(true);
    try {
      // 1. Signed upload URL for the file's real content type.
      const urlResp = await uploadUrlMutation.mutateAsync({ contentType: file.type });
      const { uploadUrl } = urlResp.data;

      // 2. PUT the bytes straight to S3.
      const putResp = await fetch(uploadUrl, {
        method: 'PUT',
        headers: { 'Content-Type': file.type },
        body: file,
      });
      if (!putResp.ok) {
        throw new Error(`Upload failed: ${putResp.statusText}`);
      }

      // 3. Finalize — this is what bumps the cache-busting timestamp.
      await uploadCompleteMutation.mutateAsync();

      toast.success(campaignKey ? 'Campaign artwork uploaded — live on all properties' : 'Banner uploaded successfully');
      await queryClient.invalidateQueries({ queryKey: ['properties'] });
      if (campaignKey) await queryClient.invalidateQueries({ queryKey: ['banner-campaigns'] });
      onUploaded?.();
    } catch (err) {
      const status = err?.response?.status;
      if (status === 409) {
        setErrors(['Upload did not finish — the image was not found on the server. Please try again.']);
      } else if (status === 422) {
        setErrors(['The server rejected this image (too large or wrong type). Try a different file.']);
      } else {
        toast.error(err?.message || 'Failed to upload banner');
      }
    } finally {
      setUploading(false);
    }
  };

  const isPending = decoding || uploading;

  return (
    <Dialog open={open} onClose={uploading ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{campaignKey ? 'Upload Campaign Artwork' : variant === 'home' ? 'Upload Home Banner' : 'Upload Property Banner'}</DialogTitle>
      <DialogContent>
        {isPending && <LinearProgress sx={{ mb: 2 }} />}

        {campaignKey && (
          <Typography variant="body2" sx={{ color: tokens.colors.warning.fg, mb: 1.5 }}>
            This artwork is shared by every property. Uploading replaces it everywhere at once.
          </Typography>
        )}

        <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1.5 }}>
          {spec.hint}
        </Typography>

        <Box
          onClick={() => fileInputRef.current?.click()}
          onDragOver={handleDragOver}
          onDragEnter={handleDragEnter}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          sx={{
            border: `2px dashed ${dragActive ? tokens.colors.primary : tokens.colors.borderStrong}`,
            borderRadius: `${tokens.radius.md}px`,
            p: 4,
            textAlign: 'center',
            cursor: 'pointer',
            backgroundColor: dragActive ? tokens.colors.primarySoft : tokens.colors.hover,
            transition: 'border-color 120ms ease, background-color 120ms ease',
            mb: 2,
          }}
        >
          <input ref={fileInputRef} type="file" accept="image/png,image/jpeg,image/webp" style={{ display: 'none' }} onChange={handleFileInputChange} />
          <Typography sx={{ color: 'text.secondary' }}>
            {file ? 'Drag another image here, or click to replace' : 'Drag an image here, or click to select (PNG, JPEG or WebP)'}
          </Typography>
        </Box>

        {file && (
          <Typography variant="body2" sx={{ color: 'text.secondary', mb: previewUrl && errors.length === 0 ? 1.5 : 0 }}>
            {file.name}
          </Typography>
        )}

        {errors.map(msg => (
          <Typography key={msg} variant="body2" sx={{ color: tokens.colors.error.fg, mt: 0.5 }}>
            {msg}
          </Typography>
        ))}
        {warnings.map(msg => (
          <Typography key={msg} variant="body2" sx={{ color: tokens.colors.warning.fg, mt: 0.5 }}>
            {msg}
          </Typography>
        ))}

        {previewUrl && errors.length === 0 && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1 }}>
              Placement preview
            </Typography>
            {variant === 'home' ? <HomePlacementPreview src={previewUrl} /> : <LandingPlacementPreview src={previewUrl} />}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={uploading}>
          Cancel
        </Button>
        <Button variant="contained" onClick={handleUpload} disabled={!canUpload}>
          Upload
        </Button>
      </DialogActions>
    </Dialog>
  );
}

/**
 * Post-login home placement: a wide 4:1 strip over two faint placeholder
 * card blocks, mirroring how the banner sits above the resident dashboard.
 */
function HomePlacementPreview({ src }) {
  return (
    <Box
      sx={{
        p: 1.5,
        borderRadius: `${tokens.radius.md}px`,
        border: `1px solid ${tokens.colors.border}`,
        backgroundColor: tokens.colors.surface,
      }}
    >
      <Box
        component="img"
        src={src}
        alt="Home banner placement preview"
        sx={{
          width: '100%',
          aspectRatio: '4 / 1',
          objectFit: 'cover',
          display: 'block',
          borderRadius: `${tokens.radius.sm}px`,
        }}
      />
      <Stack direction="row" gap={1.5} sx={{ mt: 1.5 }}>
        {[0, 1].map(i => (
          <Box
            key={i}
            sx={{
              flex: 1,
              height: 44,
              borderRadius: `${tokens.radius.sm}px`,
              backgroundColor: tokens.colors.hover,
              border: `1px solid ${tokens.colors.border}`,
            }}
          />
        ))}
      </Stack>
    </Box>
  );
}

/**
 * Pre-login landing placement: a mini split-hero — the portrait banner on the
 * left, brand + two button bars on the right — matching the resident sign-in
 * screen. The banner is `contain`ed so portrait uploads keep their true shape
 * (the panel is portrait-oriented, so a 2:3 image fills it) and off-aspect
 * uploads letterbox instead of being cropped to a misleading landscape strip.
 */
function LandingPlacementPreview({ src }) {
  return (
    <Box
      sx={{
        display: 'flex',
        gap: 2,
        height: 220,
        p: 1.5,
        borderRadius: `${tokens.radius.md}px`,
        border: `1px solid ${tokens.colors.border}`,
        backgroundColor: tokens.colors.surface,
      }}
    >
      <Box
        sx={{
          flex: '0 0 45%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          overflow: 'hidden',
          borderRadius: `${tokens.radius.sm}px`,
          backgroundColor: tokens.colors.hover,
        }}
      >
        <Box
          component="img"
          src={src}
          alt="Landing banner placement preview"
          sx={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain', display: 'block' }}
        />
      </Box>
      <Stack sx={{ flex: 1, justifyContent: 'center', gap: 1.25 }}>
        <Box sx={{ height: 12, width: '75%', borderRadius: `${tokens.radius.pill}px`, backgroundColor: tokens.colors.primarySoft }} />
        <Box sx={{ height: 12, width: '55%', borderRadius: `${tokens.radius.pill}px`, backgroundColor: tokens.colors.hover }} />
        <Box sx={{ height: 30, width: '100%', mt: 0.5, borderRadius: `${tokens.radius.sm}px`, backgroundColor: tokens.colors.primarySoft }} />
        <Box sx={{ height: 30, width: '100%', borderRadius: `${tokens.radius.sm}px`, border: `1px solid ${tokens.colors.borderStrong}` }} />
      </Stack>
    </Box>
  );
}
